import { useState, useEffect, useCallback } from 'react'
import {
  Newspaper, Search, RefreshCw, ExternalLink, Clock, Zap,
  Brain, Cloud, Shield, Cpu, Rocket, Filter,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const CATEGORY_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  ai:         { icon: Brain,     color: 'text-purple-light',  label: 'AI' },
  cloud:      { icon: Cloud,     color: 'text-cyan-light',    label: 'Cloud' },
  security:   { icon: Shield,    color: 'text-rose-light',    label: 'Security' },
  startup:    { icon: Rocket,    color: 'text-amber-light',   label: 'Startup' },
  regulation: { icon: Filter,    color: 'text-blue-light',    label: 'Regulation' },
  hardware:   { icon: Cpu,       color: 'text-emerald-light', label: 'Hardware' },
  software:   { icon: Zap,       color: 'text-blue-light',    label: 'Software' },
  general:    { icon: Newspaper, color: 'text-text-muted',    label: 'General' },
}

const SENTIMENT_DOT: Record<string, string> = { positive: 'bg-emerald', negative: 'bg-rose', neutral: 'bg-text-muted' }
const URGENCY_STYLES: Record<string, string> = { high: 'badge-red', medium: 'badge-amber', normal: '' }

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function TechNewsPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [breakingNews, setBreakingNews] = useState<any[]>([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [feedRes, breakingRes] = await Promise.allSettled([
        api.get('/api/news/feed', { params: { limit: 60, category: categoryFilter !== 'all' ? categoryFilter : undefined } }),
        api.get('/api/news/breaking'),
      ])
      if (feedRes.status === 'fulfilled') setArticles(feedRes.value.data.articles || [])
      if (breakingRes.status === 'fulfilled') setBreakingNews(breakingRes.value.data.articles || [])
    } finally {
      setLoading(false)
    }
  }, [categoryFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = articles.filter(item => {
    if (searchTerm && !item.title.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Newspaper size={24} className="text-blue" /> Tech News
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Live from The Register, CRN, Hacker News, Ars Technica, TechCrunch, BleepingComputer + 6 more
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {breakingNews.length > 0 && (
        <div className="card p-3 border border-rose/20 bg-rose/5 overflow-hidden">
          <div className="flex items-center gap-3">
            <span className="badge-red text-[10px] shrink-0"><Zap size={10} /> BREAKING</span>
            <div className="overflow-hidden whitespace-nowrap">
              <div className="inline-flex gap-12">
                {breakingNews.slice(0, 5).map((n, i) => (
                  <span key={i} className="text-sm text-text-primary">
                    <span className={clsx('inline-block w-1.5 h-1.5 rounded-full mr-2', SENTIMENT_DOT[n.sentiment] || 'bg-text-muted')} />
                    {n.title}
                    <span className="text-text-muted ml-2">({n.source})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search news..." className="input pl-9 py-2 text-xs" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button type="button" onClick={() => setCategoryFilter('all')} className={clsx('badge text-[11px] cursor-pointer transition-colors', categoryFilter === 'all' ? 'badge-blue' : 'bg-surface-hover text-text-muted hover:text-text-secondary')}>All</button>
          {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
            <button type="button" key={key} onClick={() => setCategoryFilter(key)} className={clsx('badge text-[11px] cursor-pointer transition-colors', categoryFilter === key ? 'badge-blue' : 'bg-surface-hover text-text-muted hover:text-text-secondary')}>
              <val.icon size={10} /> {val.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !articles.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching from 12+ RSS feeds...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => {
          const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.general
          const CatIcon = cat.icon
          return (
            <div key={item.id || idx} className="card-hover p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-surface-hover shrink-0 mt-0.5">
                  <CatIcon size={16} className={cat.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx('w-1.5 h-1.5 rounded-full', SENTIMENT_DOT[item.sentiment] || 'bg-text-muted')} />
                    <span className={clsx('badge text-[10px]', `${cat.color} bg-surface-hover`)}>{cat.label}</span>
                    {item.urgency === 'high' && <span className="badge-red text-[10px]">urgent</span>}
                    {item.translated && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded bg-blue/10 text-blue-light border border-blue/20 font-mono"
                        title={item.original_title ? `Original (${(item.language || '').toUpperCase()}): ${item.original_title}` : `Translated from ${(item.language || '').toUpperCase()}`}
                      >
                        {(item.language || '').toUpperCase()} → EN
                      </span>
                    )}
                    <span className="text-[10px] text-text-muted ml-auto flex items-center gap-1">
                      <Clock size={10} /> {relativeTime(item.timestamp)}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary leading-snug">
                    {item.url ? <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a> : item.title}
                  </h3>
                  <p className="text-xs text-text-secondary mt-1 leading-relaxed">{item.summary}</p>
                  <span className="text-[10px] text-text-muted mt-1.5 inline-block">
                    {item.source}
                    {item.translated && item.original_title && (
                      <span className="ml-2 italic text-text-muted/70" title={item.original_title}>
                        · original: "{item.original_title.slice(0, 80)}{item.original_title.length > 80 ? '…' : ''}"
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
        {!loading && filtered.length === 0 && <p className="text-sm text-text-muted text-center py-8">No articles match your search</p>}
      </div>
    </div>
  )
}
